import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle, Loader2 } from 'lucide-react'
import Modal from '../../../components/Modal/Modal'
import {
  useMdmAgentVersions, useMdmAgentTargets, useSetScopeAgentTarget, useSetDeviceAgentTarget,
} from '../../../hooks/useMdm'
import { formatDate } from '../../../utils/format'
import { compareAgentVersions, agentTargetSourceLabel, errorMessage } from './mobileUtils'

const INHERIT = '__inherit__'
const LATEST = 'latest'

/**
 * Escolhe a versão do agente MDM (CONTRACT.md §9) para um tablet (`state` = MdmStateDto) ou para um
 * escopo da árvore — grupo (`groupId`) ou raiz (`groupId` null). O escopo vale para os subgrupos.
 * O servidor leva cada tablet até a versão escolhida; nunca faz downgrade.
 */
export default function AgentVersionModal({ isOpen, onClose, state = null, groupId = null, groupName = null, onFeedback }) {
  const deviceMode = !!state
  const { data: versions = [], isLoading: versionsLoading, error: versionsError } = useMdmAgentVersions(isOpen)
  const { data: targets = [] } = useMdmAgentTargets(isOpen && !deviceMode)
  const setScope = useSetScopeAgentTarget()
  const setDevice = useSetDeviceAgentTarget(state?.id)
  const saving = setScope.isPending || setDevice.isPending

  const current = useMemo(() => {
    if (deviceMode) return state.deviceAgentTarget ?? null
    return targets.find((t) => (t.groupId ?? null) === (groupId ?? null))?.targetAgentVersion ?? null
  }, [deviceMode, state, targets, groupId])

  const [choice, setChoice] = useState(INHERIT)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setChoice(current ?? INHERIT)
      setError(null)
    }
  }, [isOpen, current])

  const latest = versions.find((v) => v.isLatest)?.version
  const installed = state?.agentVersion

  const inheritLabel = deviceMode
    ? (state.agentTargetSource && state.agentTargetSource !== 'device'
      ? `Herdar (hoje: ${state.agentTargetVersion || state.agentTargetSetting} ${agentTargetSourceLabel(state)})`
      : 'Herdar do grupo')
    : groupId ? 'Herdar do grupo acima' : 'Sem versão-alvo (não atualiza sozinho)'

  const apply = async () => {
    setError(null)
    const version = choice === INHERIT ? null : choice
    try {
      if (deviceMode) {
        await setDevice.mutateAsync(version)
        onFeedback?.({ type: 'ok', text: version ? `Versão do MDM deste tablet fixada em ${version === LATEST ? 'sempre a mais recente' : version}.` : 'Este tablet voltou a herdar a versão do grupo.' })
      } else {
        const res = await setScope.mutateAsync({ groupId, version })
        const n = res?.devices ?? 0
        onFeedback?.({ type: 'ok', text: `Versão do MDM ${version ? (version === LATEST ? '"sempre a mais recente"' : version) : 'herdada'} aplicada a ${groupName || 'todos os tablets'} (${n} tablet(s), incluindo subgrupos).` })
      }
      onClose?.()
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível salvar a versão.'))
    }
  }

  const title = deviceMode ? `Versão do MDM — ${state.name}` : `Versão do MDM — ${groupName || 'todos os tablets'}`

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {deviceMode
            ? <>Instalada: <strong>{installed || '—'}</strong>. A versão escolhida aqui vale só para este tablet.</>
            : <>Vale para {groupId ? 'este grupo e todos os subgrupos' : 'todos os tablets do tenant'}, exceto onde um subgrupo ou tablet tiver a própria versão. Tablets desligados atualizam quando voltarem.</>}
        </p>

        {versionsLoading ? (
          <div className="flex items-center text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin mr-2" />Carregando versões...</div>
        ) : versionsError ? (
          <p className="text-sm text-red-600">{errorMessage(versionsError, 'Erro ao carregar as versões publicadas.')}</p>
        ) : (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
            <Option value={INHERIT} choice={choice} onChange={setChoice} label={inheritLabel} />
            <Option
              value={LATEST}
              choice={choice}
              onChange={setChoice}
              label="Sempre a mais recente"
              hint={latest ? `hoje: ${latest} — atualiza sozinho a cada versão nova` : 'nenhuma versão publicada'}
            />
            {versions.map((v) => {
              const below = deviceMode && installed && compareAgentVersions(v.version, installed) < 0
              return (
                <Option
                  key={v.version}
                  value={v.version}
                  choice={choice}
                  onChange={setChoice}
                  disabled={below}
                  label={v.version}
                  hint={[
                    v.publishedAt ? `publicada em ${formatDate(v.publishedAt)}` : null,
                    v.isLatest ? 'mais recente' : null,
                    v.prerelease ? 'pré-lançamento' : null,
                    installed === v.version ? 'instalada' : null,
                    below ? 'abaixo da instalada (Android não volta versão)' : null,
                  ].filter(Boolean).join(' · ')}
                />
              )
            })}
          </div>
        )}

        {!deviceMode && choice !== INHERIT && (
          <p className="flex items-start gap-2 text-xs text-gray-500">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
            Tablets que já estão numa versão mais nova que a escolhida continuam nela — o Android não faz downgrade.
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={saving || versionsLoading || (choice ?? INHERIT) === (current ?? INHERIT)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-theme-primary rounded-lg hover:bg-theme-primary/90 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Aplicar
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Option({ value, choice, onChange, label, hint, disabled = false }) {
  return (
    <label className={clsx('flex items-start gap-2 px-3 py-2 text-sm', disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50')}>
      <input
        type="radio"
        className="mt-0.5"
        checked={choice === value}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span>
        <span className="text-gray-900">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  )
}
